import { debounce, updatePageData, trackAdHocEvent } from '@rdss/core'
import { Component, Element, Event, EventEmitter, Host, Prop, Watch, h } from '@stencil/core'
import { VariantSwitchItem } from './types'
import { Snippets } from './types/snippets'

const DefaultSnippets: Snippets = {
    VariantTitle: 'Varianten',
}

@Component({
    tag: 'zr-variant-switch',
    styleUrl: './zr-variant-switch.scss',
    scoped: true,
})
/**
 * Variant Switch Component.
 */
export class ZrVariantSwitch {
    /**
     * Snippets.
     */
    @Prop({ mutable: true }) snippets?: Snippets = DefaultSnippets

    /**
     * Variants.
     */
    @Prop() variants: VariantSwitchItem[]

    /**
     * Locale used for currencies.
     */
    @Prop() locale: string

    /**
     * Debounce time for resize event.
     */
    @Prop() debounceTime?= 350

    /**
     * Show only the variant with the given id.
     */
    @Prop() showVariantOnly: string | undefined

    @Element() host: HTMLZrVariantSwitchElement

    @Event() variantChanged: EventEmitter<{ sku: number }>

    /**
     * Watches for snippet changes and merges the default values with the
     * new prop values.
     */
    @Watch('snippets')
    setSnippets(newVal: Snippets, oldVal: Snippets) {
        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
            this.snippets = { ...this.snippets, ...newVal }
        }
    }

    variantClicked = (variant: VariantSwitchItem) => {
        if (!variant.selected && variant.isAvailable) {
            this.variantChanged.emit({ sku: variant.id })
            const urlPath = new URL(variant.url).pathname
            history.pushState({}, '', urlPath)
            updatePageData('variantClicked', variant.id)
            trackAdHocEvent('pageView')
        }
    }

    /**
     * Toggle function for scroll container of variant items.
     *
     * @param {string} direction Direction to step to.
     */
    private variantScroll(direction: string): void {
        const container = this.host.querySelector('.zr-variant-switch__container-inner') as HTMLElement
        const items = Array.from(container.querySelectorAll('.sc-zr-variant-switch-item')) as HTMLElement[]

        // calculate the min with of all items but skip items with 0 width
        let minItemWidth = items.reduce((min, item) => {
            const itemWidth = item.offsetWidth
            if (itemWidth > 0 && itemWidth < min) {
                return itemWidth
            }
            return min
        }, Number.MAX_SAFE_INTEGER)

        // use a default of 100 if no min width could be calculated
        if (minItemWidth === Number.MAX_SAFE_INTEGER) {
            minItemWidth = 100
        } else {
            // scale the the min width so the user must not click multiple times to scroll
            minItemWidth = minItemWidth * 1.5
        }

        let scrollPosition = container.scrollLeft

        if (direction === 'next') {
            scrollPosition += minItemWidth
        } else {
            scrollPosition -= minItemWidth
        }

        container.scrollLeft = scrollPosition
    }

    /**
     * Scrolls to the selected variant based on the width of the container and the items.
     */
    private scrollToVariant(): void {
        // Get the container and selected variant
        const container = this.host.querySelector('.zr-variant-switch__container-inner') as HTMLElement
        const selectedVariantElement = this.host.querySelector('.sc-zr-variant-switch-item.selected') as HTMLElement
        if (selectedVariantElement) {
            // Calculate the position of the selected variant in the container
            const variantPosition = selectedVariantElement.offsetLeft - container.offsetLeft
            // Scroll to the selected variant
            container.scrollLeft = variantPosition
        }
    }

    /**
     * Enables or disables the arrows based on the width of the container and the items.
     */
    private enableOrDisableIcons(): void {
        try {
            // if we have just one variant, never show the arrows
            if (this.variants && this.variants.length === 1) {
                return
            }

            const host = this.host || document

            const container = host.querySelector('.zr-variant-switch__container-inner') as HTMLElement
            const allVariantItems = Array.from(host.querySelectorAll('zr-variant-switch-item'))

            const leftArrows = [
                host.querySelector('.zr-variant-switch__toggle--prev'),
                host.querySelector('.zr-variant-switch__toggle--desktop--prev'),
            ]
            const rightArrows = [
                host.querySelector('.zr-variant-switch__toggle--next'),
                host.querySelector('.zr-variant-switch__toggle--desktop--next'),
            ]

            // Calculate the total width of all items and the container's width
            const totalItemWidth = allVariantItems.reduce((sum, item) => sum + (item.offsetWidth || 100), 0)
            const containerWidth = container.offsetWidth

            // Show the arrows if the total item width is greater than the container width
            const showArrows = totalItemWidth > containerWidth

            // Remove 'hidden' class from arrows if the total item width is greater than the container width
            if (showArrows) {
                leftArrows.forEach(arrow => arrow.classList.remove('hide-arrow'))
                rightArrows.forEach(arrow => arrow.classList.remove('hide-arrow'))
            }
        } catch (error) {
            console.error(error)
        }
    }

    /**
     * Lifecycle method that is called after render.
     */
    componentDidRender() {
        setTimeout(() => {
            this.scrollToVariant()
        }, 300)

        this.enableOrDisableIcons()

        // Add event listener to window to enable or disable arrows on resize
        // Debounce the resize handler to prevent it from being called too often
        const debouncedResizeHandler = debounce(this.enableOrDisableIcons, this.debounceTime)
        window.addEventListener('resize', debouncedResizeHandler as EventListener)
    }

    /**
     * Renders the component.
     *
     * @returns {JSX.Element} - The rendered component.
     */
    render() {
        return (
            <Host>
                <div class="zr-variant__wrapper">
                    <div class="zr-variant-switch__controls">
                        <p class="zr-variant-switch__headline">{this.snippets.VariantTitle}</p>
                        <div class="zr-variant-switch__arrows">
                            <div
                                class="zr-variant-switch__toggle--prev hide-arrow"
                                data-toggle="prev"
                                onClick={() => this.variantScroll('prev')}></div>
                            <div
                                class="zr-variant-switch__toggle--next hide-arrow"
                                data-toggle="next"
                                onClick={() => this.variantScroll('next')}>
                            </div>
                        </div>
                    </div>
                    <div class="zr-variant-switch__container">
                        <div
                            class="zr-variant-switch__toggle--desktop--prev hide-arrow"
                            data-toggle="prev"
                            onClick={() => this.variantScroll('prev')}></div>
                        <div
                            class="zr-variant-switch__toggle--desktop--next hide-arrow"
                            data-toggle="next"
                            onClick={() => this.variantScroll('next')}></div>
                        <div class="zr-variant-switch__container-inner">
                            {this.variants.map(variant => (
                                (!this.showVariantOnly || this.showVariantOnly === variant.id.toString()) &&
                                <zr-variant-switch-item
                                    hasLowestPricePerUnit={variant.hasLowestPricePerUnit}
                                    variant={variant}
                                    locale={this.locale}
                                    onClick={() => this.variantClicked(variant)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </Host>
        )
    }
}
